// T010: Card Model

import type {
  Card,
  CardDefinition,
  CardLocation,
  CardOrientation,
  BattlePosition,
} from '../../types';

export function createCard(
  definition: CardDefinition,
  ownerId: string,
  instanceId?: string
): Card {
  return {
    id: instanceId ?? `${definition.id}_${String(Date.now())}_${Math.random().toString(36).slice(2, 7)}`,
    definitionId: definition.id,
    ownerId,
    location: 'deck',
    orientation: 'upright',
    activeEffects: definition.effectIds,
    mergedCards: [],
    passengers: [],
  };
}

export function moveCard(
  card: Card,
  location: CardLocation,
  position?: BattlePosition
): Card {
  return {
    ...card,
    location,
    position: location === 'battlefield' ? position : undefined,
  };
}

export function setCardOrientation(
  card: Card,
  orientation: CardOrientation
): Card {
  return {
    ...card,
    orientation,
  };
}

export function addMergedCard(baseCard: Card, mergedCard: Card): Card {
  return {
    ...baseCard,
    mergedCards: [...(baseCard.mergedCards ?? []), mergedCard],
  };
}

export function addPassenger(card: Card, passenger: Card): Card {
  return {
    ...card,
    passengers: [...(card.passengers ?? []), passenger],
  };
}

export function removePassenger(card: Card, passengerId: string): Card {
  return {
    ...card,
    passengers: (card.passengers ?? []).filter((p) => p.id !== passengerId),
  };
}

export function isUnit(definition: CardDefinition): boolean {
  return definition.type === 'unit';
}

export function isSpell(definition: CardDefinition): boolean {
  return definition.type === 'spell';
}

export function isFieldSpell(definition: CardDefinition): boolean {
  return definition.type === 'spell' && definition.isField === true;
}

export function getCardAtk(
  card: Card,
  definitions: Map<string, CardDefinition>
): number {
  const def = definitions.get(card.definitionId);
  if (!def || def.type !== 'unit') return 0;

  let atk = def.atk ?? 0;

  // Add ATK from merged cards
  for (const merged of card.mergedCards ?? []) {
    const mergedDef = definitions.get(merged.definitionId);
    if (mergedDef?.type === 'unit') {
      atk += mergedDef.atk ?? 0;
    }
  }

  return atk;
}

export function getCardScore(
  card: Card,
  definitions: Map<string, CardDefinition>
): number {
  const def = definitions.get(card.definitionId);
  if (!def || def.type !== 'unit') return 0;

  let score = def.score ?? 0;

  // Add score from merged cards
  for (const merged of card.mergedCards ?? []) {
    const mergedDef = definitions.get(merged.definitionId);
    if (mergedDef?.type === 'unit') {
      score += mergedDef.score ?? 0;
    }
  }

  return score;
}
